import { useFormik } from "formik"
import React, { useState } from "react"
import { Col, FloatingLabel, Form, Row, Spinner } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import * as Yup from "yup"
import ModalComponent from "../../../components/Modal"
import TooltipComponent from "../../../components/Tooltip"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import { handleOnBlur, handleOnchange } from "../../../lib/GlobalMethods"

const AddEditBrand = ({
  isUpdate,
  setIsUpdate,
  show,
  setShow,
  fetchData,
  setData,
  handleClose,
}) => {
  const [loading, setLoading] = useState(false)

  const navigate = useNavigate()

  const validationSchema = Yup.object({
    brand_name: Yup.string()
      .min(2, "Enter at least 2 characters for Brand Name")
      .required("Brand Name is required"),
    brand_img: Yup.mixed()
      .test("required", "Brand logo is required", (file) => {
        if (isUpdate) {
          return file !== null && file !== undefined
        }
        return file
      })
      .test(
        "fileType",
        "File format is not supported, Please use .jpg/.png/.jpeg",
        (file) => {
          if (isUpdate && (!file || typeof file === "string")) return true
          return (
            file && ["image/png", "image/jpg", "image/jpeg"].includes(file.type)
          )
        }
      )
      .test(
        "fileSize",
        "File size too large, max file size is 500 Kb",
        (file) => {
          if (isUpdate && (!file || typeof file === "string")) return true
          return file && file.size <= 512000
        }
      ),
  })

  const handleSubmit = async (values, { resetForm }) => {
    try {
      values = {
        ...values,
        brand_img:
          typeof values?.brand_img === "object"
            ? values?.brand_img
            : isUpdate.brand_img,
      }

      const data = new FormData()
      for (const key in values) {
        data.append(key, values[key])
      }

      const response = await axiosProvider({
        method: isUpdate ? "PATCH" : "POST",
        endpoint: isUpdate
          ? admin.updateBrand + isUpdate.id
          : admin.createBrand,
        data: data,
      })

      if (response?.data?.code === 201 || response?.data?.code === 200) {
        toast.success(response.data.message)
        setLoading(false)
        isUpdate
          ? setData((prevData) =>
              prevData.map((item) =>
                item.id === isUpdate.id
                  ? {
                      ...item,
                      brand_name: response.data.updatedBrand[1][0].brand_name,
                      brand_img: response.data.updatedBrand[1][0].brand_img,
                    }
                  : item
              )
            )
          : fetchData("")
        setShow(false)
        setIsUpdate(false)
        resetForm()
      } else {
        setLoading(false)
        throw { message: response.data.message }
      }
    } catch (error) {
      setLoading(false)
      toast.error(error.message)
    }
  }

  const formik = useFormik({
    initialValues: isUpdate
      ? {
          brand_name: isUpdate.brand_name,
          brand_img: isUpdate.brand_img?.split("/").pop(),
        }
      : {
          brand_name: "",
          brand_img: "",
        },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  })

  const { errors, values, touched, setFieldValue, resetForm, initialValues } =
    formik

  return (
    <>
      <ModalComponent
        show={show}
        modalsize={"md"}
        backdrop="static"
        animationFade={true}
        onHide={() => {
          handleClose()
          resetForm()
        }}
        loading={loading}
        modeltitle={isUpdate ? "Update Brand" : "Add Brand"}
        submitname={
          loading ? (
            <Spinner size="sm" animation="border" variant="white" />
          ) : isUpdate ? (
            "Update"
          ) : (
            "Add"
          )
        }
        btnclosetext={"Cancel"}
        handleSubmit={formik.handleSubmit}
        children={
          <Form noValidate>
            <Row xs={"auto"}>
              <Col xs={12}>
                <FloatingLabel
                  controlId="brandname"
                  label={
                    <>
                      Brand Name <span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="brand_name"
                    autoComplete="false"
                    maxLength={30}
                    value={values.brand_name}
                    onChange={(e) => handleOnchange(e, false, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={touched.brand_name && errors.brand_name}
                    isValid={touched.brand_name && !errors.brand_name}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                      }
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.brand_name}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col xs={12}>
                <FloatingLabel
                  controlId="brandimg"
                  label={
                    <>
                      Brand Logo <span className="required_star">*</span>
                    </>
                  }
                >
                  {values?.brand_img ? (
                    <div
                      className={`previewnm_ip ${
                        touched?.brand_img && errors?.brand_img
                          ? "is-invalid"
                          : ""
                      }`}
                    >
                      <TooltipComponent
                        toolplace="top"
                        tooltipText={
                          typeof values?.brand_img === "object"
                            ? values?.brand_img.name
                            : values?.brand_img
                        }
                      >
                        <p className="mb-0 previewnm_title">
                          {typeof values?.brand_img === "object"
                            ? values?.brand_img.name
                            : values?.brand_img}
                        </p>
                      </TooltipComponent>
                      <span
                        className="remove_previewnm"
                        onClick={() => {
                          setFieldValue("brand_img", "")
                        }}
                      >
                        <i className="icon_main cross_icon"></i>
                      </span>
                    </div>
                  ) : (
                    <Form.Control
                      type="file"
                      placeholder=""
                      autoComplete="false"
                      name="brand_img"
                      onChange={(e) => {
                        setFieldValue("brand_img", e?.target?.files[0])
                      }}
                      isInvalid={touched?.brand_img && errors?.brand_img}
                      isValid={touched?.brand_img && !errors?.brand_img}
                    />
                  )}
                  <Form.Control.Feedback type="invalid">
                    {errors.brand_img}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
            </Row>
          </Form>
        }
      />
    </>
  )
}

export default AddEditBrand
